import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { contentSorting, contentType, searchTileType } from '../../../../../constants';
import { findMatchedCpdCategory, findMatchedUserCpdCategory } from '../../../../../services';
import { cpdCategorysActions } from '../../../../../state/modules/areas-of-advice';
import { publisherActions } from '../../../../../state/modules/publisher';
import { searchTileActions } from '../../../../../state/modules/search-tile';
import { userActions } from '../../../../../state/modules/user';
import { currentUserCpdWeeksRemaining } from '../../../../../state/modules/user/selectors';
import { ContentCpdCategoryFilterGroup, FormField, Icon, SearchToggler } from '../../../../components';
import { ProgressChart } from '../../../../partials';
import Loading from '../../../loading';
import ProgressCard from '../../partials/progress-card';
import ContentList, { searchTileFilter } from './content-list';

const CpdCategory = ({
  cpdCategory,
  userCpdCategory,
  cpdWeeksRemaining,
  isFetchingContent,
  searchQuery,
  searchContent,
  sort,
  sortBy,
  displaySearchBlock,
  onSearchIconClick,
  parentName,
  parentPath,
  searchLabel,
  searchTileType: searchTileTypeForComponent,
  updateFilters,
  filter
}) => (
    <div className='base-layout'>
      <section id='header--training' className='header--area-of-advice layout--header'>
        <Grid>
          <Row className='no-gutter'>
            <Col xs={12} md={7}>
              <Breadcrumb>
                <LinkContainer to={parentPath}>
                  <BreadcrumbItem>
                    {parentName}
                  </BreadcrumbItem>
                </LinkContainer>
                <BreadcrumbItem active>
                  {cpdCategory.name}
                </BreadcrumbItem>
              </Breadcrumb>
              <div className='relative-placement'>
                <div className='chart-duplicate'>
                  {
                    userCpdCategory ?
                      <ProgressChart
                        progressPercentage={userCpdCategory.progress}
                        width={100}
                        height={100} /> :
                      null
                  }
                </div>
                <h1>
                  <Icon icon='training-large' />
                  {cpdCategory.name}
                  <Icon
                    className='search-toggle-button'
                    icon='search-large'
                    onClick={(e) => onSearchIconClick(e)} />
                </h1>
              </div>
            </Col>
            {
              userCpdCategory ?
                <ProgressCard {...{
                  cpdCategory: userCpdCategory.title,
                  cpdWeeksRemaining,
                  validCpd: userCpdCategory.validCpd,
                  progress: userCpdCategory.progress,
                  goal: userCpdCategory.goal,
                  totalCpdCategorys: 1,
                  showPublish: false
                }} /> :
                <ProgressCard {...{
                  totalCpdCategorys: 0,
                  showPublish: false
                }} />
            }
          </Row>
          <Row className='no-gutter search-header'>
            <Col xs={12} md={9} className='flexbox nowrap'>
              <span className='right-gap'>
                <SearchToggler onChange={searchContent} />
              </span>
              <ContentCpdCategoryFilterGroup {...{
                onApply: updateFilters
              }} />
            </Col>
            <Col xs={12} md={3}>
              <form>
                <FormField
                  id='sort-by'
                  label='Sort by'
                  type='select-item'
                  required>
                  <select
                    id='sort-by'
                    value={sort}
                    required
                    onChange={(e) => sortBy(e)}>
                    {
                      fp.flow(
                        fp.keys,
                        fp.map((sortingKey) =>
                          <option key={sortingKey} value={sortingKey}>
                            {fp.get(sortingKey, contentSorting).displayName}
                          </option>
                        )
                      )(contentSorting)
                    }
                  </select>
                </FormField>
              </form>
            </Col>
          </Row>
        </Grid>
      </section>
      <ContentList {...{
        filter
      }} />
    </div>
  );

const mapStateToProps = (state, { cpdCategoryMatch }) => ({
  cpdWeeksRemaining: currentUserCpdWeeksRemaining(state),
  isFetchingContent:
    state.cpdCategorys.isFetching ||
    state.publisher.isFetching ||
    state.user.currentUserCpdCategorysIsFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  isFetchingCpdCategory: state.cpdCategorys.isFetching ||
    state.user.currentUserCpdCategorysIsFetching,
  userCpdCategory: (!state.cpdCategorys.isFetching && !state.user.isFetchingCpdCategoryProgress && !fp.isEmpty(state.cpdCategorys.cpdCategorys)) ?
    findMatchedUserCpdCategory(cpdCategoryMatch)(state.cpdCategorys.cpdCategorys)(state.user.cpdCategoryProgress) :
    null,
  cpdCategory: !state.cpdCategorys.isFetching ?
    findMatchedCpdCategory(
      cpdCategoryMatch,
      state.cpdCategorys.cpdCategorys
    ) :
    null,
  userState: state.user
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getSearchTile: bindActionCreators(searchTileActions.searchTile.get, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
  goTo: bindActionCreators(push, dispatch)
});

export default compose(
  withProps(({
    match: {
      params: {
        cpdCategoryId
      }
    } }) => ({
      cpdCategoryMatch: parseInt(cpdCategoryId, 10)
    })),
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    {
      displaySearchBlock: false,
      searchQuery: '',
      searchDebounce: undefined,
      sort: fp.head(fp.keys(contentSorting)),
      contentLoadingInitiated: false,
      filters: {}
    },
    {
      updateFilters: () => (filters) => ({ filters }),
      onSortByChange: () => (sort) => ({
        sort
      }),
      onSearchChange: () => (searchQuery) => ({
        searchQuery
      }),
      setSearchDebounce: () => (searchDebounce) => ({
        searchDebounce
      }),
      onSearchIconClick: ({ displaySearchBlock }) => () => ({
        displaySearchBlock: !displaySearchBlock
      }),
      onContentLoadingInitiated: () => () => ({
        contentLoadingInitiated: true
      })
    }
  ),
  withProps(({
    searchQuery,
    cpdCategory,
    sort,
    filters
  }) => {
    const selectedContentType = fp.getOr(null)(filters.contentType)(contentType)
    return {
      filter: {
        searchTileType: searchTileType.Content,
        titleTags: searchQuery,
        cpdCategorys: fp.get('id')(cpdCategory),
        sort: fp.flow(
          fp.get(sort),
          (sort) => fp.join(',')([sort.field, sort.order])
        )(contentSorting),
        minCpdHours: filters.min,
        maxCpdHours: filters.max,
        contentType: selectedContentType,
        isBookmarkedOnly: filters.isBookmarkedOnly,
      }
    }
  }),
  withHandlers({
    refetchData: ({ searchDebounce, getSearchTile, filter, setSearchDebounce }) => () => {
      searchDebounce && searchDebounce.cancel()
      const debounce = fp.debounce(500)(
        () => getSearchTile(0, filter)
      )
      setSearchDebounce(debounce)
      debounce()
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        getPublishers,
        cpdCategorys,
        isFetchingCpdCategorys,
        isFetchingPublishers,
        getCpdCategorys,
        publishers,
        getUserCpdCategorys,
        getUserBookmarks,
      } = this.props;
      if (cpdCategorys.length === 0 && !isFetchingCpdCategorys) {
        getCpdCategorys();
      }

      if (publishers.length === 0 && !isFetchingPublishers) {
        getPublishers();
      }

      getUserCpdCategorys();
      getUserBookmarks();
    },
    componentWillReceiveProps(nextProps) {
      const {
        onContentLoadingInitiated,
        cpdCategory: prevCpdCategory,
        getSearchTile,
        sort,
        filter: prevFilter
      } = this.props;
      const {
        cpdCategory,
        contentLoadingInitiated,
        filter,
        refetchData
      } = nextProps;

      if ((!prevCpdCategory && cpdCategory) || (cpdCategory && !contentLoadingInitiated)) {
        onContentLoadingInitiated();
        getSearchTile(0, searchTileFilter(searchTileType.Content, cpdCategory.id, sort));
      }

      if (JSON.stringify(filter) !== JSON.stringify(prevFilter)) {
        refetchData()
      }
    }
  }),
  withHandlers({
    sortBy: ({ onSortByChange, cpdCategory }) => (e) => {
      if (cpdCategory) {
        onSortByChange(e.target.value);
      }
    },
    searchContent: ({ onSearchChange }) => (query) => {
      onSearchChange(query);
    }
  }),
  branch(
    ({ isFetchingCpdCategory, cpdCategory }) => isFetchingCpdCategory || !cpdCategory,
    renderComponent(Loading)
  )
)(CpdCategory);



// WEBPACK FOOTER //
// ./src/containers/pages/app/training/partials/area-of-advice.js